/**
 * WCAG contrast utilities for DMC-themed quotation pages.
 *
 * A DMC may set a very light primary color in Settings (e.g. pale yellow).
 * When that color is used as TEXT on a white card, it can fail WCAG AA
 * (4.5:1). This helper darkens such colors just enough to stay legible,
 * while leaving colors that already pass completely untouched.
 */

const hexToRgb = (hex) => {
  if (!hex || typeof hex !== 'string') return null;
  const clean = hex.replace('#', '').trim();
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  if (full.length !== 6 || /[^0-9a-fA-F]/.test(full)) return null;
  const num = parseInt(full, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
};

const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b]
    .map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0'))
    .join('')}`;

const relativeLuminance = ({ r, g, b }) => {
  const toLinear = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

const contrastRatio = (rgb1, rgb2) => {
  const l1 = relativeLuminance(rgb1);
  const l2 = relativeLuminance(rgb2);
  const [lighter, darker] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (lighter + 0.05) / (darker + 0.05);
};

const WHITE = { r: 255, g: 255, b: 255 };

/**
 * Returns a version of `hex` that passes WCAG AA (4.5:1) as text on a white
 * background, darkening step-by-step until it passes. Returns `hex`
 * unchanged if it already passes or is not a valid hex color.
 */
export const getAccessibleTextColor = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  if (contrastRatio(rgb, WHITE) >= 4.5) return hex;

  let { r, g, b } = rgb;
  for (let i = 0; i < 24; i += 1) {
    r *= 0.9;
    g *= 0.9;
    b *= 0.9;
    if (contrastRatio({ r, g, b }, WHITE) >= 4.5) break;
  }
  return rgbToHex({ r, g, b });
};

const BLACK = { r: 0, g: 0, b: 0 };
const NAVY_TEXT = '#0A1F44';

/**
 * Picks readable text color ('#ffffff' or a dark navy) for a solid-fill
 * button/chip whose background is `hex` — needed because a DMC's accent
 * color (e.g. a bright yellow) can make fixed white text unreadable.
 */
export const getContrastTextColor = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#ffffff';
  const onWhiteText = contrastRatio(rgb, WHITE);
  const onDarkText = contrastRatio(rgb, BLACK);
  return onWhiteText >= onDarkText ? '#ffffff' : NAVY_TEXT;
};
